import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DndContext, useDraggable, useDroppable, DragOverlay, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import api from "@/lib/api";
import { PageHeader, LoadingSkeleton } from "@/components/shared/States";
import { RiskBadge, Avatar } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { GitBranch, Plus, Clock } from "lucide-react";
import { fmtMoney, timeAgo } from "@/lib/format";
import { toast } from "sonner";

const DealCard = ({ deal, onClick }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: deal.id, data: { deal } });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} data-testid="deal-kanban-card"
      onClick={onClick}
      className={`rounded-xl bg-white border border-slate-200 p-3 mb-2 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow ${isDragging ? "opacity-40" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-slate-900 leading-tight">{deal.name}</div>
      </div>
      <div className="text-xs text-slate-400 mt-0.5">{deal.company_name}</div>
      <div className="flex items-center justify-between mt-2.5">
        <span className="text-sm font-bold tabular-nums text-slate-900">{fmtMoney(deal.value)}</span>
        <span className="text-xs text-slate-400">{deal.probability}%</span>
      </div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
        <div className="flex items-center gap-1 text-[11px] text-slate-400"><Clock className="h-3 w-3" />{timeAgo(deal.stage_entered_at)}</div>
        {deal.ai_risk && <RiskBadge value={deal.ai_risk} />}
      </div>
    </div>
  );
};

const Column = ({ stage, deals, onCardClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id: stage.name });
  const total = deals.reduce((s, d) => s + (d.value || 0), 0);
  return (
    <div ref={setNodeRef} data-testid="pipeline-stage-column" className={`w-72 shrink-0 rounded-2xl border p-2.5 ${isOver ? "bg-violet-50 border-[#7C3AED]/40" : "bg-slate-100/60 border-slate-200"}`}>
      <div className="flex items-center justify-between px-1.5 mb-2">
        <div className="flex items-center gap-2"><span className="text-sm font-semibold text-slate-700">{stage.name}</span><span className="text-xs text-slate-400 bg-white rounded-full px-1.5">{deals.length}</span></div>
        <span className="text-xs font-medium text-slate-500 tabular-nums">{fmtMoney(total)}</span>
      </div>
      <div className="min-h-[120px] max-h-[calc(100vh-260px)] overflow-y-auto px-0.5">
        {deals.map((d) => <DealCard key={d.id} deal={d} onClick={() => onCardClick(d.id)} />)}
      </div>
    </div>
  );
};

export default function Pipeline() {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState(null);
  const [deals, setDeals] = useState(null);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", company_name: "", value: "", stage: "New Lead" });
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const load = async () => {
    const p = await api.get("/pipelines"); setPipeline(p.data.items[0]);
    const d = await api.get("/deals?limit=500"); setDeals(d.data.items);
  };
  useEffect(() => { load(); }, []);

  const onDragEnd = async ({ active, over }) => {
    setActive(null);
    if (!over) return;
    const deal = deals.find((d) => d.id === active.id);
    if (!deal || deal.stage === over.id) return;
    const stageObj = pipeline.stages.find((s) => s.name === over.id);
    setDeals((ds) => ds.map((d) => d.id === active.id ? { ...d, stage: over.id, probability: stageObj?.probability ?? d.probability } : d));
    try { await api.put(`/deals/${active.id}`, { stage: over.id, probability: stageObj?.probability }); }
    catch { toast.error("Could not move deal"); load(); }
  };
  const create = async () => { if (!form.name) return toast.error("Name required"); await api.post("/deals", { ...form, value: Number(form.value) || 0 }); toast.success("Deal created"); setOpen(false); setForm({ name: "", company_name: "", value: "", stage: "New Lead" }); load(); };

  if (!pipeline || !deals) return <LoadingSkeleton />;

  return (
    <div>
      <PageHeader title="Sales Pipeline" subtitle={`${deals.filter(d=>!['Won','Lost'].includes(d.stage)).length} open deals · ${fmtMoney(deals.filter(d=>!['Won','Lost'].includes(d.stage)).reduce((s,d)=>s+(d.value||0),0))}`} icon={GitBranch}
        actions={<Button data-testid="new-deal-button" className="aurelo-gradient text-white" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New Deal</Button>} />
      <DndContext sensors={sensors} onDragStart={({ active }) => setActive(active.data.current?.deal)} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
        <div data-testid="pipeline-kanban-board" className="flex gap-3 overflow-x-auto pb-4">
          {pipeline.stages.map((s) => <Column key={s.id} stage={s} deals={deals.filter((d) => d.stage === s.name)} onCardClick={(id) => navigate(`/app/deals/${id}`)} />)}
        </div>
        <DragOverlay>{active ? <div className="rounded-xl bg-white border-2 border-[#7C3AED] p-3 shadow-xl w-64"><div className="text-sm font-semibold">{active.name}</div><div className="text-sm font-bold mt-1">{fmtMoney(active.value)}</div></div> : null}</DragOverlay>
      </DndContext>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Deal</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Deal name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div><Label>Company</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="mt-1" /></div>
            <div><Label>Value ($)</Label><Input type="number" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="aurelo-gradient text-white" onClick={create}>Create Deal</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
